import { BulletFillIcon, BulletOutlineIcon } from '@applyboard/ui-icons';
import React from 'react';
import { IconType, Intent, useFullTheme } from '~/design';
import { InternalText } from '~/internal/InternalText';
import { allowedTooltipSymbols } from '~/utils/tooltip';
import { TooltipCompatibleProps } from '~/internal/InternalTooltip';
import { IconWrapper } from '~/internal';

export interface TagProps extends TooltipCompatibleProps {
  /** The aria-label of the Tag. Only applicable when the Tag is rendered as a button and has no
   * text. */
  'aria-label'?: string;

  /** The element type to render the Tag as. Use `as=button` if you require onClick handling or if
   * you want to attach a tooltip to the Tag. */
  as?: 'button';

  /** The text of the tag. */
  children?: React.ReactNode;

  /** The emphasis of the tag against its background. (default: 'highlighted') */
  emphasis?: 'contained' | 'highlighted';

  /** The icon that appears on the left side of the tag. */
  icon?: 'fill' | 'outline' | IconType;

  /** The intent of the tag. Maps to colour. */
  intent: Intent;

  /** A callback function that is invoked when the Tag is clicked. Only applicable when the `as`
   * prop is set to `button`. */
  onClick?: () => void;

  /** Dictates how the text overflows. */
  overflow?: 'ellipsis' | 'normal';

  /** The size of the tag. */
  size?: 'sm' | 'md' | 'lg';

  /** The icon that appears on the right side of the tag. */
  trailIcon?: IconType;
}

export const Tag = React.forwardRef<HTMLButtonElement, TagProps>(function Tag(
  {
    'aria-label': ariaLabel,
    as,
    children,
    emphasis = 'highlighted',
    icon,
    intent,
    onBlur,
    onClick,
    onFocus,
    onPointerDown,
    onPointerLeave,
    onPointerMove,
    overflow = 'normal',
    size = 'sm',
    trailIcon: TrailIcon,
  },
  ref,
) {
  if (!intent) {
    throw new Error('`intent` prop is required.');
  }

  const theme = useFullTheme();
  const isIconOnly = !children && !!icon;

  const paddingXWithIcon = {
    sm: theme.spacing[1],
    md: theme.spacing[1],
    lg: theme.spacing[2],
  };

  const paddingXDefault = {
    sm: theme.spacing[2],
    md: theme.spacing[2],
    lg: theme.spacing[4],
  };

  const heightMap = {
    sm: 24,
    md: 32,
    lg: 40,
  };

  const iconMap = {
    fill: BulletFillIcon,
    outline: BulletOutlineIcon,
  };

  const AsTag = as === 'button' ? 'button' : 'div';
  const isLegacyIcon = typeof icon === 'string' && ['fill', 'outline'].includes(icon);
  const Icon = (!icon ? null : isLegacyIcon ? iconMap[icon] : icon) as IconType;

  const additionalProps =
    AsTag !== 'button'
      ? {}
      : {
          onBlur,
          onClick,
          onFocus,
          onPointerDown,
          onPointerLeave,
          onPointerMove,
          ref,
        };

  const paddingInlineStart = Icon
    ? isLegacyIcon
      ? paddingXWithIcon[size]
      : isIconOnly
        ? paddingXDefault[size] // isIconOnly and not legacy icon needs some visual compensation
        : theme.spacing[2]
    : paddingXDefault[size];

  const paddingInlineEnd = isIconOnly
    ? isLegacyIcon
      ? paddingXWithIcon[size]
      : paddingXDefault[size]
    : paddingXDefault[size];

  return (
    // @ts-expect-error This is caused by the `ref` prop. TS doesn't handle "dynamic" ref types
    // very well (e.g. ref for button element, no ref for div element)
    <AsTag
      aria-label={as === 'button' && !children ? ariaLabel : undefined}
      css={[
        {
          backgroundColor:
            emphasis === 'highlighted'
              ? {
                  magic: theme.colors.bgMagic,
                  negative: theme.colors.bgNegative,
                  positive: theme.colors.bgPositive,
                  primary: theme.colors.bgPrimary,
                  secondary: theme.colors.bgSecondary,
                  warning: theme.colors.bgWarning,
                }[intent]
              : {
                  magic: theme.colors.actionMagic,
                  negative: theme.colors.actionNegative,
                  positive: theme.colors.actionPositive,
                  primary: theme.colors.actionPrimary,
                  secondary: theme.colors.actionSecondary,
                  warning: theme.colors.actionWarning,
                }[intent],
          borderRadius: '24px',
          display: 'inline-flex',
          height: heightMap[size],
          paddingInline: 0,
          paddingInlineStart,
          paddingInlineEnd,
          maxWidth: overflow === 'ellipsis' ? '100%' : undefined,
          whiteSpace: 'nowrap',
        },
        AsTag === 'button' && {
          border: 0,
          paddingBlock: 0,
          alignItems: 'center',
        },
      ]}
      {...additionalProps}
    >
      <div
        css={[
          {
            display: 'flex',
            alignItems: 'center',
            gap: size === 'lg' ? theme.spacing[2] : theme.spacing[1],
            whiteSpace: 'nowrap',
          },
          overflow === 'ellipsis' && {
            overflowX: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          },
        ]}
      >
        {Icon && (
          <IconWrapper allowInteractions={false} intent={intent} selector="div">
            <Icon size={isLegacyIcon ? 'md' : size} />
          </IconWrapper>
        )}
        {children && (
          <InternalText
            as="span"
            contrast={emphasis === 'contained' ? 'inverted' : undefined}
            intent={intent}
            variant={size === 'sm' ? 'labelS' : 'labelM'}
            overflow="ellipsis"
          >
            {children}
          </InternalText>
        )}
        {TrailIcon && (
          <IconWrapper allowInteractions={false} intent={intent} selector="div">
            <TrailIcon size={size} />
          </IconWrapper>
        )}
      </div>
    </AsTag>
  );
});

Object.assign(Tag, { [allowedTooltipSymbols.Tag]: true });
